/**
 * Lightning Dash — Plan 4b Task 3.1
 *
 * Directional item. Walks forward up to 10 landable board-graph tiles in chosen direction.
 * Stops on the active treasure chest if reached.
 * Deals 15 damage to each player on traversed tiles (excluding start).
 * "Beside" players (besideGraveyardId != null) are immune.
 * Stone Skin blocks per-victim.
 * Returns endsTurn:false — player rolls AFTER per spec §4 #8.
 *
 * Self-registers via addItem(lightningDash) at module load.
 */
import type { GameState } from '../types';
import type { ItemCastCtx, ItemCastResult, Item } from './types';
import type { BoardGraph } from '@shared/board/graph';
import { walkForwardRay } from '@shared/board/targeting';
import { appendEvent } from '../event-log';
import { applyDamage } from '../damage';
import { addItem } from './registry';
import { clearLastMove } from '../movement-options';
import { tryBlockWithStoneSkin } from '../stone-skin-block';

/** Maximum number of landable graph tiles the dash can travel. */
export const LIGHTNING_DASH_MAX_HOPS = 10;

/** Flat damage dealt to each player on the dash path. */
export const LIGHTNING_DASH_DAMAGE = 15;

export const lightningDash: Item = {
  id: 'lightning_dash',
  name: 'Lightning Dash',
  description:
    'Dash forward up to 10 tiles in chosen direction, dealing 15 damage to each player in the path. ' +
    'Can stop on a treasure chest. Player rolls AFTER. Blocked by Stone Skin per-victim.',
  targetType: 'direction',
  category: 'complex',
  rarity: 'uncommon',
  blockedByStoneSkin: true,

  cast(state: GameState, ctx: ItemCastCtx): ItemCastResult {
    const { casterId } = ctx;
    const direction = ctx.target.direction ?? { dx: 1, dy: 0 };
    const graph = ctx.graph as BoardGraph | undefined;

    // Step 1: Validate caster and graph
    const caster = state.players.get(casterId);
    if (!caster) throw new Error(`Lightning Dash: caster not found: ${casterId}`);
    if (!graph) throw new Error('Lightning Dash: graph required for direction-based traversal');

    // Step 2: Emit item_cast
    appendEvent(state, {
      type: 'item_cast',
      playerId: casterId,
      casterId,
      itemId: 'lightning_dash',
      direction,
    });

    // Step 3: Walk up to LIGHTNING_DASH_MAX_HOPS landable tiles along walk edges.
    const fromNodeId = caster.nodeId;
    const ray = walkForwardRay(graph, fromNodeId, direction, {
      maxLandableTiles: LIGHTNING_DASH_MAX_HOPS,
      stopAtNodeId: state.treasureChestNodeId || null,
    });
    const path = ray.path;
    const toNodeId = path.length > 0 ? ray.finalNodeId : fromNodeId;

    if (path.length > 0) {
      caster.nodeId = toNodeId;
      clearLastMove(state, casterId);
    }

    appendEvent(state, {
      type: 'item_dash',
      playerId: casterId,
      casterId,
      itemId: 'lightning_dash',
      path,
      fromNodeId,
      toNodeId,
    });

    // Step 4: Apply damage to players on each traversed node in path order.
    for (const step of ray.steps) {
      for (const playerId of state.turnOrder) {
        const player = state.players.get(playerId);
        if (player == null) continue;
        if (playerId === casterId) continue; // never self
        if (player.isDead) continue;
        if (player.nodeId !== step.nodeId) continue;

        // "Beside" graveyard immunity (spec §4 #8)
        if (player.besideGraveyardId !== null) continue;

        if (
          tryBlockWithStoneSkin(state, playerId, {
            actorId: casterId,
            sourceId: 'lightning_dash',
          })
        ) {
          continue;
        }

        appendEvent(state, {
          type: 'item_dash_hit',
          playerId: casterId,
          casterId,
          itemId: 'lightning_dash',
          victimId: playerId,
          damageDealt: LIGHTNING_DASH_DAMAGE,
          nodeId: step.nodeId,
          pathIndex: step.pathIndex,
        });
        applyDamage(state, playerId, LIGHTNING_DASH_DAMAGE, 'item_lightning_dash', casterId);
      }
    }

    const forcedRelocations =
      path.length === 0
        ? undefined
        : [
            {
              playerId: casterId,
              actorId: casterId,
              fromNodeId,
              toNodeId,
              reason: 'dash' as const,
              source: 'lightning_dash' as const,
            },
          ];

    return { success: true, endsTurn: false, forcedRelocations };
  },
};

// Self-register at module load
addItem(lightningDash);
